import { Calendar } from "components/generic/Calendar";
import { Col } from "components/primitives";
import { useConfirm } from "contexts/ConfirmContext";
import { DashboardContext } from "contexts/DashboardContext";
import { usePropChangedEffect } from "hooks/propChangedEffect";
import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { WidgetFC } from "types/Widget";
import { getDateString } from "util/getDateString";
import { v4 as uuidv4 } from "uuid";

import { CalendarDateFormats } from "./CalendarDateFormats";
import { CurrentEventsModal } from "./CurrentEventsModal";
import { CalendarItemData } from "./interfaces";

export interface CalendarState {
  selectedDateString: string; // JSON.parse does not correctly parse dates
  month: number;
  year: number;
}

const BuildDefaultEvent = (): CalendarItemData => ({
  id: uuidv4(),
});

export const CalendarWidget: WidgetFC<CalendarState> = ({
  state: { selectedDateString, month, year },
  setState,
  settings,
}) => {
  const { start, prevDays: colourPrevDays, format, view } = settings;
  const selectedDate = new Date(selectedDateString);
  const selectedMonth = selectedDate.getMonth();
  const selectedYear = selectedDate.getFullYear();
  const [showEventsModal, setShowEventsModal] = useState(false);
  const {
    data: { assignedEvents },
    setData: setGlobalData,
  } = useContext(DashboardContext);
  const [newAssignedEvents, setNewAssignedEvents] = useState(assignedEvents);
  const confirm = useConfirm();

  usePropChangedEffect(() => {
    setMonth(selectedMonth, selectedYear);
  }, [selectedMonth, selectedYear]);

  const setSelectedDate = (selectedDate: Date) => {
    setState({ selectedDateString: selectedDate.toDateString() });
    setShowEventsModal(true);
  };

  const setMonth = (month: number, year: number) => {
    setState({ month, year });
  };

  const deleteSelectedEvent = async (id: string) => {
    const confirmed = await confirm({
      title: "Info",
      message: "Are you sure you want to remove this event?",
      confirmText: "Ok",
      cancelText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    // if (!window.confirm("Are you sure you want to remove this event?")) {
    //   return false;
    // }

    const dateString = getDateString(selectedDate);

    const updatedEvents = { ...newAssignedEvents };

    if (typeof updatedEvents[dateString] === "undefined") {
      return;
    }

    const newEventsForDate = updatedEvents[dateString].filter((item) => item.id !== id);

    if (newEventsForDate.length === 0) {
      delete updatedEvents[dateString];
    } else {
      updatedEvents[dateString] = newEventsForDate;
    }

    setNewAssignedEvents(updatedEvents);
    setGlobalData({
      assignedEvents: updatedEvents,
    });
  };

  const updateSelectedEvent = (newEventData: CalendarItemData) => {
    const dateKey = getDateString(selectedDate);

    const updatedEvents = { ...newAssignedEvents };

    if (updatedEvents[dateKey]) {
      const eventIndex = updatedEvents[dateKey].findIndex((event) => event.id === newEventData.id);

      if (eventIndex !== -1) {
        updatedEvents[dateKey][eventIndex] = newEventData;
      } else {
        updatedEvents[dateKey].push(newEventData);
      }
    } else {
      updatedEvents[dateKey] = [newEventData];
    }

    setNewAssignedEvents(updatedEvents);
    setGlobalData({
      assignedEvents: updatedEvents,
    });
  };

  const handleAddEvent = () => {
    const newEvent = BuildDefaultEvent();
    const dateKey = getDateString(selectedDate);

    const updatedEvents = { ...newAssignedEvents };
    if (updatedEvents[dateKey]) {
      updatedEvents[dateKey][updatedEvents[dateKey].length] = newEvent;
    } else {
      updatedEvents[dateKey] = [newEvent];
    }
    setNewAssignedEvents(updatedEvents);
    setGlobalData({
      assignedEvents: updatedEvents,
    });
  };

  return (
    <OuterContainer>
      {view === "Month" && (
        <>
          <CalendarFrame>
            <Calendar
              selectedDate={selectedDate}
              month={month}
              year={year}
              dateSelected={(date) => setSelectedDate(date)}
              monthChanged={setMonth}
              startOfWeek={start}
              colourPrevDays={colourPrevDays}
              assignedEvents={newAssignedEvents}
            />
          </CalendarFrame>
          <SelectedDate>{CalendarDateFormats[format](selectedDate)}</SelectedDate>
        </>
      )}
      {view === "Day" && (
        <FocusContainer>
          <span>Today is:</span>
          <FocusedDate>{CalendarDateFormats[format](new Date())}</FocusedDate>
        </FocusContainer>
      )}
      {showEventsModal && (
        <>
          <CurrentEventsModal
            currentEvents={newAssignedEvents?.[getDateString(selectedDate)]}
            hasClosed={() => setShowEventsModal(false)}
            deleteSelectedEvent={deleteSelectedEvent}
            handleAddEvent={handleAddEvent}
            onEditSubmit={updateSelectedEvent}
          />
        </>
      )}
    </OuterContainer>
  );
};

const OuterContainer = styled(Col)`
  align-items: center;
  height: 100%;
  justify-content: flex-start;
  padding-top: 3cqh;
`;

const SelectedDate = styled.p`
  font-weight: 600;
  font-size: 4cqw;
  width: 100%;
  text-align: center;
  margin: auto;
`;

const FocusContainer = styled(Col)`
  width: 85%;
  height: 100%;
  align-items: center;
  justify-content: center;
  gap: 12cqh;

  span {
    color: black;
    font-weight: 600;
    font-size: 6cqw;
    line-height: 1em;
  }
`;

const CalendarFrame = styled.div`
  width: 85cqw;
  aspect-ratio: 1;
`;

const FocusedDate = styled.p`
  font-weight: 600;
  font-size: 12cqw;
  width: 100%;
  text-align: center;
  line-height: 1.33em;
`;
